<template>
  <transition
    class="transform-gpu"
    @enter="onEnter"
    @leave="onLeave"
    @css="false"
    :mode="mode"
  >
    <div v-if="visible" class="relative z-1">
      <slot />
    </div>
  </transition>
</template>

<script setup lang="ts">
import { type BaseTransitionProps } from 'vue';

const useClamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  duration: {
    type: Number,
    default() {
      return 1500;
    },
  },
  mode: {
    type: String as PropType<BaseTransitionProps['mode']>,
    default: 'in-out',
  },
});

const onEnter: BaseTransitionProps['onEnter'] = (element, done) => {
  // Get elements
  const childrenCollection = element.children as HTMLCollectionOf<HTMLElement>;
  const children = Array.from(childrenCollection);

  // Set props
  element.style.height = 'auto';
  const height = element.offsetHeight;
  const speed = props.duration;

  // Set init values
  element.style.position = 'relative';
  element.style.zIndex = '1';
  element.style.overflow = 'hidden';
  element.style.opacity = '0';
  element.style.height = '0';
  if (children.length > 0) {
    children.forEach((child, index) => {
      child.style.opacity = '0';
      child.style.transform = 'scale(0)';
      child.style.transformOrigin = 'center center';
    });
  }

  // Start animation
  const start = performance.now();
  const animate = (time: number) => {
    const elapsed = time - start;
    const parentProgress = useClamp(
      easeInOutCubic((elapsed * 2) / speed),
      0,
      1,
    ); // parent faster than child
    const progress = easeInOutCubic(elapsed / speed);

    if (progress > 1) {
      element.removeAttribute('style');
      if (children.length > 0)
        children.forEach((child) => child.removeAttribute('style'));
      done();
    } else {
      element.style.height = `${height * parentProgress}px`;
      element.style.opacity = `${parentProgress}`;
      if (children.length > 0) {
        children.forEach((child, idx) => {
          const progressWithDelay = useClamp(
            easeInOutCubic((elapsed * 2) / speed - 0.1 * idx),
            0,
            1,
          );
          child.style.opacity = `${progressWithDelay}`;
          child.style.transform = `scale(${1 * progressWithDelay})`;
        });
      }
      requestAnimationFrame(animate);
    }
  };
  requestAnimationFrame(animate);
};

const onLeave: BaseTransitionProps['onEnter'] = (element, done) => {
  // Get elements
  const childrenCollection = element.children as HTMLCollectionOf<HTMLElement>;
  const children = Array.from(childrenCollection);

  // Set props
  const height = element.offsetHeight;
  const speed = props.duration;

  // Set init values
  element.style.opacity = '1';
  element.style.position = 'relative';
  element.style.zIndex = '1';
  element.style.overflow = 'hidden';
  element.style.height = `${height}px`;
  if (children.length > 0) {
    children.forEach((child) => {
      child.style.opacity = '1';
    });
  }

  // Start animation
  const start = performance.now();
  const animate = (time: number) => {
    const elapsed = time - start;
    const progress = easeInOutCubic(elapsed / speed);
    const childProgress = easeInOutCubic((elapsed * 2) / speed);
    const parentProgress = useClamp(
      easeInOutCubic((elapsed * 2) / speed),
      0,
      1,
    ); // parent faster than child
    if (progress > 1) {
      element.removeAttribute('style');
      if (children.length > 0)
        children.forEach((child) => child.removeAttribute('style'));
      done();
    } else {
      element.style.height = `${height * (1 - parentProgress)}px`;
      element.style.opacity = `${1 - parentProgress}`;
      if (children.length > 0) {
        children.forEach((child) => {
          child.style.opacity = `${1 - childProgress}`;
          child.style.transform = `scale(${1 - 1 * childProgress})`;
        });
      }
      requestAnimationFrame(animate);
    }
  };
  requestAnimationFrame(animate);
};

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
</script>
